/**
 * BenchmarkTaskPage (`/evals/benchmarks/:slug/tasks/:taskSlug`) — one task:
 * header stats, the golden-process timeline with step detail, and tabs for
 * the rollout viewer, the runs table, and the step-outcome grid. Model and
 * rollout selection live in URL params (?model= & ?rollout=) so deep
 * links work; the rollout itself streams from the bucket on selection.
 */

import { useRef, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { getApiErrorDetail, getApiErrorStatus } from "@/api/client";

import { GoldenProcessTimeline } from "@/components/benchmarks/GoldenProcessTimeline";
import { ProcessRunsTable } from "@/components/benchmarks/ProcessRunsTable";
import { StepOutcomeGrid } from "@/components/benchmarks/StepOutcomeGrid";
import { StepRail } from "@/components/benchmarks/StepRail";
import { TrajectoryTree } from "@/components/benchmarks/TrajectoryTree";
import { trajectoryToEvents } from "@/components/evals/trajectoryEvents";
import { Markdown } from "@/components/review/Markdown";
import { TranscriptEvents } from "@/components/review/TrajectoryTranscript";
import { deriveDifficulty } from "@/components/benchmarks/benchmarkAnalysis";
import {
  DIFFICULTY_LABELS,
  DIFFICULTY_TONES,
  scoreTone,
  VERDICT_FILLS,
  VERDICT_TONES,
} from "@/components/evals/analysis";
import { EvalBreadcrumb } from "@/components/evals/EvalBreadcrumb";
import { formatScore } from "@/components/evals/format";
import { processRolloutForEntry } from "@/components/benchmarks/goldenProcess";
import { RolloutStrip } from "@/components/benchmarks/RolloutStrip";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { PageContainer } from "@/components/ui/page-container";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EmptyState } from "@/components/ui/empty-state";
import { LoadingPanel } from "@/components/shared/LoadingScreen";
import { useMinimumLoading } from "@/hooks/useMinimumSplash";
import {
  useBenchmark,
  useRollout,
  useRolloutList,
  useTaskProcess,
} from "@/hooks/useBenchmarks";
import { cn } from "@/lib/utils";
import type {
  BenchmarkRubricItem,
  BenchmarkScore,
  BenchmarkTask,
  CriterionAggregate,
  RolloutDetailResponse,
  RolloutEntry,
  TaskProcessResponse,
  VerifierResult,
} from "@/api/generated/model";

/** Verifier statuses that put the warning flag on the header. */
const FLAGGED_VERIFIER_STATUSES = new Set([
  "suspected_broken",
  "needs_review",
  "grading_issue",
]);

/**
 * Task-level rubric card: each criterion with its pass tally across ALL
 * rollouts (bar + count). Per-rollout verdicts live in GradesCard beside
 * the trajectory they grade.
 */
function RubricCard({
  rubric,
  aggregates,
  gradedCount,
  totalCount,
}: {
  rubric: BenchmarkRubricItem[];
  aggregates: CriterionAggregate[] | undefined;
  gradedCount: number;
  totalCount: number;
}): React.JSX.Element {
  const count = rubric.length > 0 ? rubric.length : (aggregates?.length ?? 0);
  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Rubric · {count} criteria
          {totalCount > 0 &&
            ` · ${gradedCount.toString()} of ${totalCount.toString()} rollouts graded`}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Bar per criterion — height is its pass rate across ALL
            rollouts, same dialect as the rollout strip. Full criterion
            text + verdicts live in the Grades card. */}
        {aggregates !== undefined && aggregates.length > 0 ? (
          <div className="flex flex-wrap items-end gap-1">
            {aggregates.map((aggregate) => {
              if (aggregate.graded_count === 0) return null;
              const rate = aggregate.pass_count / aggregate.graded_count;
              return (
                <Popover key={aggregate.id}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          aria-label={`Criterion: ${aggregate.criterion}`}
                          className="w-4 rounded"
                          style={{
                            height: 8 + rate * 20,
                            backgroundColor: scoreTone(rate),
                          }}
                        />
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-80">
                      <p className="text-xs">{aggregate.criterion}</p>
                      <p className="font-mono text-xs text-muted-foreground">
                        {aggregate.pass_count.toString()}/
                        {aggregate.graded_count.toString()} rollouts pass
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <PopoverContent className="max-h-[var(--radix-popover-content-available-height)] w-[28rem] max-w-[90vw] overflow-y-auto text-sm">
                    <p className="text-foreground">{aggregate.criterion}</p>
                    <p className="mt-1 font-mono text-xs text-muted-foreground">
                      {aggregate.pass_count.toString()}/
                      {aggregate.graded_count.toString()} rollouts pass
                    </p>
                    {aggregate.sample_rationale !== "" && (
                      <div className="mt-2 border-t border-border pt-2 text-xs text-foreground">
                        <Markdown text={aggregate.sample_rationale} />
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No graded rollouts yet.
          </p>
        )}
        {(() => {
          const graded = (aggregates ?? []).filter((a) => a.graded_count > 0);
          if (graded.length === 0) return null;
          const rates = graded.map((a) => a.pass_count / a.graded_count);
          const average =
            rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
          const alwaysPass = rates.filter((rate) => rate === 1).length;
          const neverPass = rates.filter((rate) => rate === 0).length;
          return (
            <p className="flex flex-wrap items-center gap-1.5 font-mono text-xs text-muted-foreground">
              <span className="text-foreground">{formatScore(average)}</span>
              <span>avg pass rate</span>
              <span>· {alwaysPass.toString()} always pass</span>
              <span>· {neverPass.toString()} never pass</span>
            </p>
          );
        })()}
      </CardContent>
    </Card>
  );
}

/**
 * The SELECTED rollout's grades: PASS/FAIL per criterion with the
 * judge's rationale behind a dropdown. Sits in the rail beside the
 * trajectory so rationale reads against the messages it judges.
 */
function GradesCard({
  results,
  score,
}: {
  results: VerifierResult[];
  score: number | null;
}): React.JSX.Element | null {
  const [openKey, setOpenKey] = useState<string | null>(null);
  if (results.length === 0) {
    // An ungraded rollout must say so: 40% of the GDPVal delivery shipped
    // with no judge results, and a silently absent card reads as a bug.
    return (
      <Card>
        <CardContent className="pt-4">
          <p className="text-sm font-medium text-muted-foreground">Grades · —</p>
          <p className="mt-1 text-xs text-muted-foreground">
            The delivery has no judge results for this rollout.
          </p>
        </CardContent>
      </Card>
    );
  }

  const fill = (passed: boolean | null): string | undefined => {
    if (passed === true) return VERDICT_FILLS.pass;
    if (passed === false) return VERDICT_FILLS.fail;
    return undefined;
  };
  const tone = (passed: boolean | null): string | undefined => {
    if (passed === true) return VERDICT_TONES.pass;
    if (passed === false) return VERDICT_TONES.fail;
    return undefined;
  };

  return (
    <Card>
      <CardContent className="pt-4">
        <details>
          <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
            Grades{score !== null && ` · ${formatScore(score)}`}
          </summary>
          <div className="mt-2 space-y-1.5">
            {results.map((result) => (
              <div
                key={result.id}
                className="overflow-hidden rounded-md border border-border bg-background"
                style={{ borderColor: tone(result.passed) }}
              >
                <button
                  type="button"
                  disabled={result.rationale === ""}
                  onClick={() => {
                    setOpenKey(openKey === result.id ? null : result.id);
                  }}
                  className={cn(
                    "flex w-full items-start justify-between gap-3 p-2 text-left",
                    result.rationale !== "" && "cursor-pointer",
                  )}
                  style={{ backgroundColor: fill(result.passed) }}
                >
                  <span className="text-sm text-foreground">
                    {result.criterion !== "" ? result.criterion : result.id}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    {result.passed !== null ? (
                      <Badge
                        variant="outline"
                        style={{
                          color: tone(result.passed),
                          borderColor: tone(result.passed),
                        }}
                      >
                        {result.passed ? "pass" : "fail"}
                      </Badge>
                    ) : (
                      result.score !== null && (
                        <Badge variant="outline">
                          {formatScore(result.score)}
                        </Badge>
                      )
                    )}
                  </span>
                </button>
                {openKey === result.id && result.rationale !== "" && (
                  <div
                    className="border-t border-border p-2 text-xs text-foreground"
                    style={{ borderColor: tone(result.passed) }}
                  >
                    <Markdown text={result.rationale} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </details>
      </CardContent>
    </Card>
  );
}

/** The rollout's diagnosis, in the rail beside the transcript. */
function DiagnosisCard({
  rollout,
}: {
  rollout: RolloutDetailResponse;
}): React.JSX.Element | null {
  const diagnosis = rollout.diagnosis;
  if (diagnosis === null || diagnosis === undefined) return null;
  return (
    <Card>
      <CardContent className="pt-4">
        <details>
          <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
            Diagnosis
            <Badge variant="outline" className="ml-2">
              {diagnosis.label}
            </Badge>
            {diagnosis.confidence !== "" && (
              <span className="ml-2 text-xs">
                confidence {diagnosis.confidence}
              </span>
            )}
          </summary>
          <div className="mt-2 space-y-1 text-sm">
            {diagnosis.failed_step !== "" && (
              <p className="text-muted-foreground">
                Failed step: {diagnosis.failed_step}
              </p>
            )}
            {diagnosis.root_cause !== "" && (
              <p className="text-foreground">{diagnosis.root_cause}</p>
            )}
            {diagnosis.fix_recommendation !== "" && (
              <p className="text-muted-foreground">
                Fix: {diagnosis.fix_recommendation}
              </p>
            )}
          </div>
        </details>
      </CardContent>
    </Card>
  );
}

/** The task's expected answer, collapsed until asked for. */
function GoldResponseCard({
  task,
}: {
  task: BenchmarkTask;
}): React.JSX.Element | null {
  if (task.gold_response === "") return null;
  return (
    <Card>
      <CardContent className="pt-4">
        <details>
          <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
            Gold response
            {task.gold_response_type !== "" && (
              <span className="ml-2 font-mono text-xs">
                {task.gold_response_type}
              </span>
            )}
          </summary>
          <p className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap text-sm leading-6 text-foreground">
            {task.gold_response}
          </p>
        </details>
      </CardContent>
    </Card>
  );
}

function TaskStatsHeader({
  task,
  taskScores,
  headline,
  process,
}: {
  task: BenchmarkTask;
  taskScores: BenchmarkScore[];
  headline: number | null;
  process: TaskProcessResponse | undefined;
}): React.JSX.Element {
  const totalRollouts = taskScores.reduce((sum, s) => sum + s.n_rollouts, 0);
  const turns = (process?.rollouts ?? [])
    .map((r) => r.n_turns)
    .sort((a, b) => a - b);
  const medianTurns =
    turns.length > 0 ? turns[Math.floor(turns.length / 2)] : null;
  // Below three models the per-model cards row is dead space — the
  // stat line already fits every number.
  const showModelCards = taskScores.length >= 3;
  return (
    <div className="border-y border-border py-4">
      <dl className="flex flex-wrap gap-x-10 gap-y-4">
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            avg score
          </dt>
          <dd className="font-mono text-sm text-foreground">
            {formatScore(headline)}
            <span className="ml-2 text-xs text-muted-foreground">
              {totalRollouts} rollouts
            </span>
          </dd>
        </div>
        {medianTurns !== null && (
          <div>
            <dt className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              median turns
            </dt>
            <dd className="font-mono text-sm text-foreground">{medianTurns}</dd>
          </div>
        )}
        {!showModelCards &&
          taskScores.map((score) => (
            <div key={score.model}>
              <dt className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {score.model}
              </dt>
              <dd className="font-mono text-sm text-foreground">
                {formatScore(score.mean_score)}
                <span className="ml-2 text-xs text-muted-foreground">
                  {score.n_rollouts} runs
                </span>
              </dd>
            </div>
          ))}
      </dl>
      {showModelCards && (
        <div className="mt-4 flex flex-wrap gap-3">
          {taskScores.map((score) => (
            <div
              key={score.model}
              className="rounded-md border border-border px-3 py-2"
            >
              <p className="font-mono text-xs text-muted-foreground">
                {score.model}
              </p>
              <p className="font-mono text-sm text-foreground">
                {formatScore(score.mean_score)}
                <span className="ml-2 text-xs text-muted-foreground">
                  {score.n_rollouts} runs
                </span>
              </p>
            </div>
          ))}
        </div>
      )}
      {task.prompt !== "" && (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-muted-foreground">
            <span className="font-medium">Prompt</span>
            <span className="ml-2 text-xs">
              {task.prompt.replace(/\s+/g, " ").slice(0, 90)}…
            </span>
          </summary>
          <p className="mt-2 whitespace-pre-wrap rounded bg-muted p-3 text-sm text-foreground">
            {task.prompt}
          </p>
        </details>
      )}
    </div>
  );
}

export default function BenchmarkTaskPage(): React.JSX.Element {
  const { slug, taskSlug } = useParams<{ slug: string; taskSlug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: detail, isLoading, isError, error } = useBenchmark(slug);
  const [selectedStep, setSelectedStep] = useState(0);
  const [tab, setTab] = useState("rollouts");
  const transcriptRef = useRef<HTMLDivElement>(null);

  const task = detail?.tasks.find((t) => t.slug === taskSlug);
  const taskScores =
    detail?.scores.filter((s) => s.task_slug === taskSlug) ?? [];
  const selectedModelParam = searchParams.get("model");
  const rolloutParam = searchParams.get("rollout");

  const { data: rolloutList, isLoading: rolloutListLoading } = useRolloutList(
    slug,
    taskSlug,
  );
  const {
    data: process,
    isError: processError,
    error: processErrorBody,
  } = useTaskProcess(slug, taskSlug);
  const entries = rolloutList?.rollouts ?? [];
  // Default to the best-scoring rollout so the strip and viewer land on
  // the most interesting trace immediately.
  const defaultEntry = entries.reduce<RolloutEntry | undefined>(
    (best, entry) =>
      best === undefined || (entry.score ?? -1) > (best.score ?? -1)
        ? entry
        : best,
    undefined,
  );
  const selectedEntry =
    entries.find(
      (entry) =>
        entry.model === selectedModelParam && entry.id === rolloutParam,
    ) ?? defaultEntry;
  const model = selectedEntry?.model;
  const rolloutId = selectedEntry?.id;
  const { data: rollout, isLoading: rolloutLoading } = useRollout(
    slug,
    taskSlug,
    model,
    rolloutId,
  );

  // Selecting a rollout from any tab (runs table, outcome grid, tree
  // chip) opens its transcript, so the click has a visible result.
  const selectRollout = (entry: RolloutEntry): void => {
    setTab("rollouts");
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set("model", entry.model);
        next.set("rollout", entry.id);
        return next;
      },
      { replace: true },
    );
  };

  // Cover the whole body until the page's STRUCTURE (benchmark detail +
  // rollout list) is in — rendering before the list lands flashes a
  // misleading "no rollouts" layout. Selecting a rollout later only
  // re-covers the trajectory area below, not the page.
  const pageLoading = useMinimumLoading(isLoading || rolloutListLoading);

  const headline =
    taskScores.length > 0
      ? taskScores.reduce((sum, s) => sum + s.mean_score, 0) / taskScores.length
      : null;
  const difficulty = deriveDifficulty(headline);

  const selectedProcessRollout =
    process !== undefined && model !== undefined && rolloutId !== undefined
      ? processRolloutForEntry(
          { model, id: rolloutId },
          entries,
          process.rollouts,
        )
      : undefined;

  const verifierFlagged =
    task !== undefined &&
    (FLAGGED_VERIFIER_STATUSES.has(task.verifier_status) ||
      task.verifier_issue !== "");

  return (
    <PageContainer className="space-y-6">
      <EvalBreadcrumb
        crumbs={[
          { label: "Evals", to: "/evals" },
          {
            label: detail?.manifest.display_name ?? slug ?? "",
            to: `/evals/benchmarks/${slug ?? ""}`,
          },
          { label: task?.name ?? taskSlug ?? "" },
        ]}
      />

      {pageLoading && !isError && <LoadingPanel />}
      {isError && slug !== undefined && getApiErrorStatus(error) === 404 && (
        <Navigate to={`/evals/benchmarks/${slug}`} replace />
      )}
      {isError && getApiErrorStatus(error) !== 404 && (
        <p className="text-sm text-destructive">
          Failed to load benchmark: {getApiErrorDetail(error) ?? "unknown error"}
        </p>
      )}
      {!pageLoading && !isError && detail && !task && (
        <EmptyState
          title="Task not found"
          description={`No task ${taskSlug ?? ""} in this benchmark.`}
        />
      )}

      {!pageLoading && !isError && detail && task && (
        <>
          <div className="min-w-0 space-y-1">
            <h1 className="font-serif text-2xl font-medium text-foreground">
              {task.name}
            </h1>
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <span>
                {task.section || "uncategorized"}
                {task.world !== "" ? ` · ${task.world}` : ""}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="size-2 rounded-full"
                  style={{ backgroundColor: DIFFICULTY_TONES[difficulty] }}
                />
                {DIFFICULTY_LABELS[difficulty]}
              </span>
              {verifierFlagged && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="border-brand-neutral-olive text-brand-neutral-olive"
                    >
                      verifier issue
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-96">
                    <p className="text-xs">
                      {task.verifier_issue_detail !== ""
                        ? task.verifier_issue_detail
                        : task.verifier_issue !== ""
                          ? task.verifier_issue
                          : task.verifier_status}
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
              {task.fairness !== "" && (
                <Badge variant="outline" className="text-muted-foreground">
                  fairness {task.fairness}
                </Badge>
              )}
              <Badge variant="outline" className="text-muted-foreground">
                {task.training_readiness.split("_").join(" ")}
              </Badge>
            </p>
          </div>

          <TaskStatsHeader
            task={task}
            taskScores={taskScores}
            headline={headline}
            process={process}
          />

          {processError && getApiErrorStatus(processErrorBody) !== 404 && (
            <p className="text-sm text-destructive">
              Failed to load the golden-process analysis:{" "}
              {getApiErrorDetail(processErrorBody) ?? "unknown error"}
            </p>
          )}

          <Tabs value={tab} onValueChange={setTab}>
            {process !== undefined && (
              <TabsList>
                <TabsTrigger value="rollouts">Rollouts</TabsTrigger>
                <TabsTrigger value="runs">Runs</TabsTrigger>
                <TabsTrigger value="outcomes">Step outcomes</TabsTrigger>
                <TabsTrigger value="tree">Tree</TabsTrigger>
              </TabsList>
            )}
            <TabsContent value="rollouts" className="space-y-4">
              {entries.length > 0 && (
                <RubricCard
                  rubric={task.rubric}
                  aggregates={rolloutList?.criteria}
                  gradedCount={entries.filter((e) => e.score !== null).length}
                  totalCount={entries.length}
                />
              )}
              {/* Task-level first; the strip sits directly above the
                  transcript it selects. The timeline draws from
                  process.rollouts, so it renders even with no strip
                  entries. */}
              {process !== undefined && (
                <GoldenProcessTimeline
                  process={process}
                  selectedStep={selectedStep}
                  onSelectStep={setSelectedStep}
                  selectedRollout={selectedProcessRollout}
                  entries={entries}
                  onOpenRollout={selectRollout}
                />
              )}
              {entries.length > 0 && (
                <RolloutStrip
                  rollouts={entries}
                  selected={
                    selectedEntry !== undefined
                      ? {
                          model: selectedEntry.model,
                          rolloutId: selectedEntry.id,
                        }
                      : undefined
                  }
                  onSelect={selectRollout}
                  caption={
                    rollout !== undefined
                      ? `${rollout.trajectory.messages.length.toString()} messages`
                      : undefined
                  }
                />
              )}
              {(rolloutLoading || rollout) && (
                <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
                  {/* Left column swaps with the selected rollout; the right
                      column's task-level cards stay mounted so a rollout
                      click does not flash them through a reload. */}
                  {rolloutLoading || rollout === undefined ? (
                    <LoadingPanel />
                  ) : (
                    <div className="flex items-start gap-3">
                      {process !== undefined &&
                        selectedProcessRollout !== undefined && (
                          <StepRail
                            process={process}
                            rollout={selectedProcessRollout}
                            messages={rollout.trajectory.messages}
                            listRef={transcriptRef}
                            selectedStep={selectedStep}
                            onSelectStep={setSelectedStep}
                          />
                        )}
                      <div className="min-w-0 flex-1 space-y-2">
                        <h3 className="text-sm font-medium text-muted-foreground">
                          Trajectory · {rollout.trajectory.messages.length}{" "}
                          messages
                        </h3>
                        <div ref={transcriptRef}>
                          <TranscriptEvents
                            events={trajectoryToEvents(
                              rollout.trajectory.messages,
                              `${model ?? ""}/${rolloutId ?? ""}`,
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="space-y-4 lg:sticky lg:top-4">
                    <GoldResponseCard task={task} />
                    {!rolloutLoading && rollout && (
                      <>
                        <DiagnosisCard rollout={rollout} />
                        <GradesCard
                          results={rollout.grades.verifier_results}
                          score={rollout.grades.score}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
              {!rolloutLoading &&
                !rollout &&
                rolloutList &&
                entries.length === 0 && (
                  <EmptyState
                    title="No rollouts exported"
                    description="This task has scores but no curated rollouts in the bucket yet."
                  />
                )}
            </TabsContent>
            {process !== undefined && (
              <TabsContent value="runs">
                <ProcessRunsTable
                  rollouts={process.rollouts}
                  entries={entries}
                  selected={
                    selectedEntry !== undefined
                      ? {
                          model: selectedEntry.model,
                          rolloutId: selectedEntry.id,
                        }
                      : undefined
                  }
                  onSelect={selectRollout}
                />
              </TabsContent>
            )}
            {process !== undefined && (
              <TabsContent value="outcomes">
                <StepOutcomeGrid
                  process={process}
                  entries={entries}
                  selected={
                    selectedEntry !== undefined
                      ? {
                          model: selectedEntry.model,
                          rolloutId: selectedEntry.id,
                        }
                      : undefined
                  }
                  onSelect={selectRollout}
                />
              </TabsContent>
            )}
            {process !== undefined && (
              <TabsContent value="tree">
                <TrajectoryTree
                  process={process}
                  entries={entries}
                  selected={
                    selectedEntry !== undefined
                      ? {
                          model: selectedEntry.model,
                          rolloutId: selectedEntry.id,
                        }
                      : undefined
                  }
                  onSelect={selectRollout}
                />
              </TabsContent>
            )}
          </Tabs>

          {taskScores.length === 0 && (
            <EmptyState
              title="No scores for this task"
              description="No model has a scored run in the export."
            />
          )}
        </>
      )}
    </PageContainer>
  );
}
